import { CONFERENCE_JOINED, CONFERENCE_LEFT, ENDPOINT_MESSAGE_RECEIVED } from '../base/conference/actionTypes';
import { getCurrentConference } from '../base/conference/functions';
import { PARTICIPANT_JOINED, PARTICIPANT_LEFT } from '../base/participants/actionTypes';
import { participantJoined, participantLeft } from '../base/participants/actions';
import { getLocalParticipant } from '../base/participants/functions';
import { FakeParticipant } from '../base/participants/types';
import MiddlewareRegistry from '../base/redux/MiddlewareRegistry';
import { SET_SGI_SESSION } from './actionTypes';
import { clearSgiSession, setSgiState } from './actions';
import { SGI_COMMAND, SGI_DEBUG, SGI_PARTICIPANT_ID, SGI_PARTICIPANT_NAME } from './constants';
import { getSgiSession, getSgiSharedState, isSgiSessionActive } from './functions';
/**
 * SGI Middleware.
 *
 * Handles:
 * - Conference command listener for SGI state broadcast
 * - Moderator departure detection and auto-session-end
 * - Session lifecycle management.
 */
MiddlewareRegistry.register((store) => (next) => (action) => {
    const { dispatch, getState } = store;
    switch (action.type) {
        case CONFERENCE_JOINED: {
            const { conference } = action;
            if (SGI_DEBUG) {
                console.log('[SGI] Conference joined, middleware active');
            }
            // LATE JOINER: Send HELLO to discover any active SGI sessions
            // This must happen on CONFERENCE_JOINED (local user join), not PARTICIPANT_JOINED
            setTimeout(() => {
                const state = getState();
                const existingSession = getSgiSession(state);
                // Only send HELLO if we don't already have a session
                if (!existingSession?.active && conference) {
                    if (SGI_DEBUG) {
                        console.log('[SGI DEBUG] Sending HELLO broadcast to discover active sessions...');
                    }
                    const commandData = {
                        status: 'hello',
                        timestamp: Date.now()
                    };
                    conference.sendEndpointMessage('', {
                        name: SGI_COMMAND,
                        value: 'sgi-hello',
                        attributes: commandData
                    });
                }
            }, 1000); // 1 second delay to ensure DataChannel is ready
            break;
        }
        case ENDPOINT_MESSAGE_RECEIVED: {
            // Handle SGI messages via Redux action (standard Jitsi pattern)
            const { participant, data } = action;
            if (SGI_DEBUG) {
                console.log('[SGI DEBUG] ENDPOINT_MESSAGE_RECEIVED action:', data?.name, data);
            }
            if (data && data.name === SGI_COMMAND) {
                const senderId = participant?._id || participant?.getId?.() || 'unknown';
                if (SGI_DEBUG) {
                    console.log(`[SGI DEBUG] Processing SGI command: ${data.attributes?.status} from ${senderId}`);
                }
                handleSGICommand(store, { value: data.value, attributes: data.attributes }, senderId);
            }
            break;
        }
        case CONFERENCE_LEFT: {
            // Clean up SGI session when leaving conference
            if (isSgiSessionActive(getState())) {
                dispatch(clearSgiSession());
            }
            break;
        }
        case PARTICIPANT_LEFT: {
            const state = getState();
            const session = getSgiSession(state);
            // Check if the leaving participant is the SGI moderator
            if (session?.active && session.moderatorId === action.participant.id) {
                if (SGI_DEBUG) {
                    console.log('[SGI] Moderator left, ending session');
                }
                // Remove fake participant
                const conference = getCurrentConference(state);
                dispatch(participantLeft(SGI_PARTICIPANT_ID, conference, {
                    fakeParticipant: FakeParticipant.SharedVideo
                }));
                // Clear session
                dispatch(clearSgiSession());
            }
            break;
        }
        case PARTICIPANT_JOINED: {
            const state = getState();
            const session = getSgiSession(state);
            const conference = getCurrentConference(state);
            const { participant } = action;
            const localParticipant = getLocalParticipant(state);
            // Skip fake participants
            if (participant.fakeParticipant) {
                break;
            }
            // PUSH LOGIC: If we are the moderator of an active session,
            // push session state to new participants immediately
            if (session?.active && session.moderatorId === localParticipant?.id) {
                if (SGI_DEBUG) {
                    console.log('[SGI] New participant joined, pushing session state:', participant.id);
                }
                const sgiState = state['features/sgi'];
                const commandData = {
                    status: 'start',
                    sessionId: session.sessionId,
                    moderatorId: session.moderatorId,
                    moderatorName: session.moderatorName,
                    timestamp: session.startedAt,
                    state: sgiState.sharedState || undefined
                };
                // Wait for participant's DataChannel to be ready (takes ~2-3 seconds)
                // Using multiple attempts with increasing delays
                const sendSessionToParticipant = (attempt) => {
                    if (attempt > 3) {
                        console.error('[SGI] Failed to send session after 3 attempts to:', participant.id);
                        return;
                    }
                    try {
                        if (conference) {
                            if (SGI_DEBUG) {
                                console.log(`[SGI DEBUG] Sending session to ${participant.id}, attempt ${attempt}`);
                            }
                            conference.sendEndpointMessage(participant.id, {
                                name: SGI_COMMAND,
                                value: session.sessionId,
                                attributes: commandData
                            });
                        }
                    }
                    catch (err) {
                        console.warn(`[SGI] Send failed (attempt ${attempt}), retrying...`, err);
                        setTimeout(() => sendSessionToParticipant(attempt + 1), 2000);
                    }
                };
                // First attempt after 3 seconds
                setTimeout(() => sendSessionToParticipant(1), 3000);
            }
            break;
        }
        case SET_SGI_SESSION: {
            // When setting a new session, ensure fake participant exists
            const state = getState();
            const conference = getCurrentConference(state);
            if (conference && !action._fakeParticipantCreated) {
                // Check if fake participant already exists
                const existingParticipant = state['features/base/participants']?.remote?.get(SGI_PARTICIPANT_ID);
                if (!existingParticipant) {
                    dispatch(participantJoined({
                        conference,
                        fakeParticipant: FakeParticipant.SharedVideo,
                        id: SGI_PARTICIPANT_ID,
                        name: action.moderatorName || SGI_PARTICIPANT_NAME
                    }));
                }
            }
            break;
        }
    }
    return next(action);
});
/**
 * Handle incoming SGI commands from other participants.
 *
 * @param {IStore} store - The redux store.
 * @param {Object} data - The received command, with its value and attributes.
 * @param {string} senderId - Id of the participant that sent the command.
 * @returns {void}
 */
function handleSGICommand(store, data, senderId) {
    const { dispatch, getState } = store;
    const state = getState();
    const localParticipant = getLocalParticipant(state);
    const conference = getCurrentConference(state);
    // Don't process our own commands
    if (senderId === localParticipant?.id) {
        return;
    }
    const { value: sessionId, attributes } = data;
    const { status, moderatorId, moderatorName } = attributes;
    if (SGI_DEBUG) {
        console.log(`[SGI DEBUG] Received command: ${status} from: ${senderId}`);
    }
    switch (status) {
        case 'hello': {
            // HANDSHAKE STEP 2: Moderator receives Hello
            const currentSession = getSgiSession(state);
            // If we are the moderator of an active session, reply with START
            if (currentSession?.active && currentSession.moderatorId === localParticipant?.id) {
                if (SGI_DEBUG) {
                    console.log(`[SGI DEBUG] Handshake: Replying to ${senderId} with session state`);
                }
                const sgiState = state['features/sgi'];
                const replyData = {
                    status: 'start',
                    sessionId: currentSession.sessionId,
                    moderatorId: currentSession.moderatorId,
                    moderatorName: currentSession.moderatorName,
                    timestamp: currentSession.startedAt,
                    state: sgiState.sharedState || undefined
                };
                if (conference) {
                    conference.sendEndpointMessage(senderId, {
                        name: SGI_COMMAND,
                        value: currentSession.sessionId,
                        attributes: replyData
                    });
                }
            }
            break;
        }
        case 'start': {
            // Another moderator started a session.
            //
            // Two participants can click the button at nearly the same instant, each starting
            // its own session. Both sides resolve the clash identically - earliest start wins,
            // session id breaks an exact tie - so the call converges on a single owner. Without
            // this, each peer would adopt the other's session and nobody would be the owner.
            const runningSession = getSgiSession(state);
            if (runningSession?.active && runningSession.sessionId !== sessionId) {
                const incomingWins = attributes.timestamp < runningSession.startedAt
                    || (attributes.timestamp === runningSession.startedAt
                        && sessionId < runningSession.sessionId);
                if (!incomingWins) {
                    break;
                }
            }
            dispatch({
                type: SET_SGI_SESSION,
                sessionId,
                moderatorId,
                moderatorName,
                startedAt: attributes.timestamp,
                _fakeParticipantCreated: false
            });
            // Set initial shared state if provided (for late joiners)
            if (attributes.state) {
                dispatch(setSgiState(attributes.state));
            }
            // Create fake participant for display
            if (conference) {
                dispatch(participantJoined({
                    conference,
                    fakeParticipant: FakeParticipant.SharedVideo,
                    id: SGI_PARTICIPANT_ID,
                    name: moderatorName || SGI_PARTICIPANT_NAME
                }));
            }
            break;
        }
        case 'update': {
            // State update from moderator
            const currentSession = getSgiSession(state);
            // Only accept updates for the current session from the moderator
            if (currentSession?.sessionId === sessionId
                && currentSession.moderatorId === moderatorId) {
                if (attributes.state) {
                    // Every update is sent twice (unicast and broadcast), so copies can arrive
                    // out of order. Without this check a late copy would overwrite newer state.
                    const currentVersion = getSgiSharedState(state)?.version ?? -1;
                    if (attributes.state.version > currentVersion) {
                        dispatch(setSgiState(attributes.state));
                    }
                }
            }
            break;
        }
        case 'stop': {
            // Session ended by moderator
            const currentSession = getSgiSession(state);
            if (currentSession?.sessionId === sessionId) {
                // Remove fake participant
                if (conference) {
                    dispatch(participantLeft(SGI_PARTICIPANT_ID, conference, {
                        fakeParticipant: FakeParticipant.SharedVideo
                    }));
                }
                // Clear local state
                dispatch(clearSgiSession());
            }
            break;
        }
    }
}
// Also export the middleware for the legacy approach
export * from './middleware.any';
